import { SOCIAL_FRAMES, type SocialFrame, type SocialPlatform } from '@pulsify/shared/dto'

export type PostFormat = SocialFrame

/**
 * Target frame each platform renders a post in. Anything outside it is cropped
 * away by the platform, so the composer previews the frame and burns the user's
 * chosen section into the upload. The measurements themselves live in
 * SOCIAL_FRAMES, shared with the AI Studio converter presets.
 */
const POST_FORMATS: Record<SocialPlatform, Record<string, PostFormat>> = {
  facebook: {
    post: SOCIAL_FRAMES.facebookPost,
    story: SOCIAL_FRAMES.story,
    reel: SOCIAL_FRAMES.story,
  },
  instagram: {
    post: SOCIAL_FRAMES.square,
    story: SOCIAL_FRAMES.story,
    reel: SOCIAL_FRAMES.story,
  },
}

/**
 * Total by design: the frame is what drives the re-encode to JPEG, so an
 * unmapped type must still yield one rather than let the composer publish the
 * source bytes in whatever format they happen to carry.
 */
export function getPostFormat(platform: SocialPlatform, type: string): PostFormat {
  return POST_FORMATS[platform]?.[type] ?? SOCIAL_FRAMES.square
}

/**
 * A cross-post fans one image out to several platforms, so the section that
 * survives everywhere is the narrowest frame of the selected set.
 */
export function getNarrowestFormat(platforms: SocialPlatform[], type: string): PostFormat {
  const formats = platforms.map((platform) => getPostFormat(platform, type))
  if (formats.length === 0) return SOCIAL_FRAMES.square
  return formats.reduce((narrowest, format) =>
    format.width / format.height < narrowest.width / narrowest.height ? format : narrowest,
  )
}
